import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { DrawerProps, WithStyles } from '@material-ui/core';
import { CircularProgress, withStyles } from '@material-ui/core';
import { Button, Modal, Text, View } from '@zinc/ui';
import { Icons } from 'bz-core-controls';
import React, { useEffect, useRef, useState } from 'react';
import styles from './styles';
import { SimpleScrollPreserver } from './useSimpleScrollPreservation';

export interface IProps extends Omit<DrawerProps, 'classes'>, WithStyles<typeof styles> {
  header: React.ReactNode;
  headerInnerClass?: string;
  headerDataTestId?: string;
  isLoading?: boolean;
  large?: boolean;
  open?: boolean;
  small?: boolean;
  drawerClasses?: DrawerProps['classes'];
  bodyRef?: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  dialogWidth?: string;
  transparentOverlay?: boolean;
  preserveScroll?: boolean;
}

const SlideDialog: React.FC<IProps> = ({
  children,
  classes,
  header,
  headerInnerClass,
  isLoading,
  large,
  small,
  onClose,
  bodyRef,
  drawerClasses,
  headerDataTestId = '',
  open: isOpen = true,
  dialogWidth = '500px',
  transparentOverlay = false,
  preserveScroll = true,
  ...drawerProps
}) => {
  const [open, setOpen] = useState(false);
  const componentIsMounted = useRef(true);

  useEffect(() => {
    setOpen(!!isOpen);
    return () => {
      componentIsMounted.current = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (componentIsMounted.current && open && !isOpen) {
      setOpen(false);
    }
  }, [isOpen, open]);

  const contents = isLoading ? (
    <div className={classes.loadingStyle}>
      <CircularProgress />
    </div>
  ) : (
    children
  );

  const className = React.useMemo(() => {
    const classNames = [classes.root];

    if (small) {
      classNames.push(classes.small);
    }

    if (large) {
      classNames.push(classes.large);
    }

    if (drawerProps.className) {
      classNames.push(drawerProps.className);
    }

    return classNames.join(' ');
  }, [classes, small, large, drawerProps.className]);
  return (
    <Modal
      transparentOverlay={transparentOverlay}
      active={open}
      onClose={onClose}
      position={{ s: 'end' }}
      size={{ s: dialogWidth, m: dialogWidth }}
    >
      <SimpleScrollPreserver>
        <Modal.Title>
          <View
            className="grid grid-cols-[1fr_auto] items-center gap-2 py-2"
            data-testid={headerDataTestId}
          >
            <View direction="row" gap={2} justify="space-between">
              {header}
            </View>

            <Button
              onClick={onClose}
              variant="ghost"
              size="large"
              icon={<FontAwesomeIcon icon={faClose} size="lg" />}
              attributes={{ 'data-testid': 'close', 'aria-label': 'Close' }}
            />
          </View>
        </Modal.Title>
        {contents}
      </SimpleScrollPreserver>
    </Modal>
  );
};

SlideDialog.defaultProps = {
  isLoading: false,
  large: false,
  small: false
};

export default withStyles(styles)(SlideDialog);

interface ISlideDialogHeaderProps {
  icon?: React.ReactNode;
  heading?: React.ReactNode;
  actions?: React.ReactNode;
}

const SlideDialogHeader: React.FC<ISlideDialogHeaderProps> = ({ icon, heading, actions }) => {
  return (
    <View className="grid grid-cols-[1fr_auto] items-center gap-2 mr-2">
      <View direction="row" align="center" gap={2}>
        {icon && icon}
        <Text>{heading}</Text>
      </View>
      <View direction="row" align="center" gap={2}>
        {actions && (
          <View direction="row" justify="end" gap={2}>
            {actions}
          </View>
        )}
      </View>
    </View>
  );
};

export { SlideDialogHeader };
